import { Paperclip, UploadCloud } from 'lucide-react';
import { useCallback, useState } from 'react';
import {
  Control,
  ControllerRenderProps,
  FieldValues,
  Path,
} from 'react-hook-form';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/ui/utils';

interface FormInputProps<T extends FieldValues> {
  name: Path<T>;
  type?: string;
  icon?: string;
  label?: string;
  control: Control<T>;
  pattern?: string;
  preIcon?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  maxLength?: number;
  className?: string;
  placeholder?: string;
  description?: string;
  autoComplete?: string;
  onFormat?: (e: React.ChangeEvent<HTMLInputElement>) => string | number;
  onChangeHandler?: (isChanged: boolean) => void;
}

const FormInput = <T extends FieldValues>({
  type = 'text',
  name,
  icon,
  label,
  preIcon,
  control,
  pattern,
  readOnly,
  disabled,
  maxLength,
  className,
  placeholder,
  description,
  autoComplete,
  onFormat,
  onChangeHandler,
}: FormInputProps<T>) => {
  const [initialValue] = useState<string | undefined>(undefined);

  const renderFileInput = useCallback(
    (field: ControllerRenderProps<T, Path<T>>) => {
      const fileInputClasses = cn(
        'focus-visible:ring-ring flex h-9 w-full items-center gap-2 rounded-md border',
        'border-input bg-transparent px-3 py-1 text-base text-muted-foreground shadow-sm',
        'transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium',
        'file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none',
        'focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
        className
      );

      return (
        <div className="relative">
          <Input
            {...field}
            id={field.name}
            type="file"
            value={field.value}
            pattern={pattern}
            maxLength={maxLength}
            className={cn('hidden', className)}
          />
          <div className={fileInputClasses}>
            {icon === 'upload' ? (
              <UploadCloud className="size-4" />
            ) : (
              <Paperclip className="size-4" />
            )}
            <label htmlFor={field.name}>{placeholder || 'Upload File'}</label>
          </div>
        </div>
      );
    },
    [className, icon, maxLength, pattern, placeholder]
  );

  const renderDisabledInput = useCallback(
    (value: string | number) => (
      <div className="cursor-pointer border-none text-typo-gray-secondary">
        {value ? (preIcon ? `$${Number(value).toLocaleString()}` : value) : '-'}
      </div>
    ),
    [preIcon]
  );

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      field: ControllerRenderProps<T, Path<T>>
    ) => {
      const newValue =
        type === 'number' ? Number(e.target.value) : e.target.value;

      if (onFormat) {
        field.onChange(onFormat(e));
      } else {
        field.onChange(newValue);
      }

      onChangeHandler?.(newValue !== initialValue);
    },
    [type, onFormat, onChangeHandler, initialValue]
  );

  return (
    <FormField
      name={name}
      control={control}
      render={({ field }) => (
        <FormItem className={cn(className)}>
          {label && (
            <FormLabel htmlFor={name} className={'as-title-03 as-strong'}>
              {label}
            </FormLabel>
          )}
          <FormControl>
            {type === 'file' ? (
              renderFileInput(field)
            ) : disabled ? (
              renderDisabledInput(field.value)
            ) : (
              <div className="relative flex items-center">
                {preIcon && (
                  <span className="absolute left-3 text-gray-500">$</span>
                )}
                <Input
                  {...field}
                  id={name}
                  type={type}
                  value={field.value || ''}
                  pattern={pattern}
                  maxLength={maxLength}
                  className={preIcon ? 'pl-8' : ''}
                  spellCheck={type === 'text'}
                  autoComplete={autoComplete}
                  placeholder={placeholder}
                  onChange={(e) => handleInputChange(e, field)}
                  readOnly={readOnly}
                />
              </div>
            )}
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export { FormInput };
